import { isValidElement, Component } from 'react';
import { uniqueId } from 'lodash';
import { Button } from '@client/components/common/Button/Button';
import { Dialog } from './Modal/Dialog';
import type { ConstructedButton, ModalProps, ModalState } from './Modal/ModalProps.interface';

export class Modal extends Component<unknown, ModalState> {
  static Instance;

  static FORM_NAME = 'sd-modal-form';

  static BUTTONS = (): any => ({
    CANCEL: (
      <Button
        id="sda-modal-cancel-button"
        type="button"
        key="button-cancel"
        aria-label="close"
        variant="outlined"
        scope="cancel-button"
        color="primary"
        onClick={() => { Modal.hide(); }}
      >
        Cancel
      </Button>
    ),
    OK: (
      <Button
        id="sda-modal-ok-button"
        type="button"
        key="button-ok"
        aria-label="close"
        variant="contained"
        scope="continue-button"
        color="primary"
        onClick={() => { Modal.hide(); }}
      >
        Continue
      </Button>
    ),
    SAVE: (
      <Button
        id="sda-modal-save-button"
        type="submit"
        scope="dialog-button"
        key="button-save"
        aria-label="close"
        variant="contained"
        color="primary"
        form={Modal.FORM_NAME}
        className="mobileFullWidth"
        onSuccess={() => { Modal.hide(); }}
      >
        Submit
      </Button>
    ),
  });

  static hide() {
    if (Modal.Instance) {
      Modal.Instance.setState({
        open: false,
        dialogContentComponent: null,
        title: null,
        media: null,
        body: null,
        buttons: [],
      });
    }
  }

  static setTitle(title: JSX.Element) {
    if (Modal.Instance) {
      Modal.Instance.setState({
        options: {
          ...Modal.Instance.state.options,
          title,
        },
      });
    }
  }

  static show({
    backgroundColor = 'inherit',
    dialogContentComponent = null,
    dialogTitleCondensed = false,
    fullScreen = false,
    fullWidth = false,
    maxWidth = 'modal',
    DialogTitleProps = {},
    subtitle = '',
    title = '',
    media = '',
    body = null,
    scroll = 'paper',
    overlay = true,
    buttons = [Modal.BUTTONS().OK],
    onClose = () => {},
    onSuccess = () => { Modal.hide(); },
    disableBackdropClick = false,
    disableCloseButton = false,
    disableEscapeKeyDown = false,
    disableGutters = false,
  }: ModalProps) {
    const compiledButtons = buttons.map((button) => {
      if (!isValidElement(button)) {
        const { label, onClick, ...buttonProps } = button as ConstructedButton;

        return (
          <Button
            type="button"
            key={`button-${uniqueId()}`}
            scope="custom-button"
            {...buttonProps}
            onClick={() => {
              Modal.Instance.state.options.onSuccess();
              onClick();
            }}
          >
            {label}
          </Button>
        );
      }

      return button;
    });

    if (Modal.Instance) {
      Modal.Instance.setState({
        open: true,
        options: {
          ...Modal.Instance.state.options,
          backgroundColor,
          buttons: compiledButtons,
          body,
          dialogContentComponent,
          dialogTitleCondensed,
          disableBackdropClick,
          disableCloseButton,
          disableEscapeKeyDown,
          disableGutters,
          DialogTitleProps,
          fullScreen,
          fullWidth,
          maxWidth,
          media,
          onClose,
          onSuccess,
          overlay,
          scroll,
          subtitle,
          title,
        },
      });
    } else {
      console.warn('No modal found'); // eslint-disable-line
      console.warn(body); // eslint-disable-line
    }
  }

  constructor(props: never) {
    super(props);

    this.state = {
      open: false,
      options: {
        backgroundColor: 'inherit',
        body: '',
        buttons: [Modal.BUTTONS().OK],
        dialogContentComponent: null,
        dialogTitleCondensed: false,
        disableBackdropClick: false,
        disableCloseButton: false,
        disableEscapeKeyDown: false,
        disableGutters: false,
        DialogTitleProps: {},
        fullScreen: false,
        fullWidth: false,
        maxWidth: false,
        media: '',
        onClose: () => {},
        onSuccess: () => {},
        overlay: true,
        scroll: 'paper',
        subtitle: '',
        title: '',
      },
    };

    Modal.Instance = this;
  }

  handleClose = () => {
    Modal.Instance.state.options.onClose();

    this.setState({
      open: false,
    });
  };

  render() {
    const { open, options } = this.state;

    return (
      <Dialog
        open={open}
        handleClose={this.handleClose}
        {...options}
      />
    );
  }
}
